import Decimal from "decimal.js";
import { asBondPriceNominal, BondPriceNominal, Money, usd } from "@goodfill/domain-properties";

const TEN_THOUSAND_USD = new Decimal(10_000);
const MAX_COMMISSION = new Decimal(250);
const MIN_COMMISSION = new Decimal(2);

const COMMISSION_RATE_FOR_THE_FIRST_10_K = new Decimal(0.002);
const COMMISSION_RATE_AFTER_THE_FIRST_10_K = new Decimal(0.0005);

export function calculateGoodfillTransactionCommissionForAmount(
  fullTransactionFaceAmount: Money,
): GoodfillTransactionCommission {
  const transactionCommissionAmount = calculateCommissionAmount(fullTransactionFaceAmount);
  const transactionCommissionRate = transactionCommissionAmount.amount.div(fullTransactionFaceAmount.amount);

  return {
    transactionFaceAmount: fullTransactionFaceAmount,
    commissionAmount: transactionCommissionAmount,
    commissionRate: asBondPriceNominal(transactionCommissionRate),
  };
}

//@link to Confluence project: https://goodfill.atlassian.net/wiki/spaces/GFAP/pages/1036550145/Changing+Goodfill+commission+fees
function calculateCommissionAmount(fullTransactionFaceAmount: Money): Money {
  let commissionAmount: Decimal;

  if (fullTransactionFaceAmount.amount.lessThanOrEqualTo(TEN_THOUSAND_USD)) {
    commissionAmount = fullTransactionFaceAmount.amount.mul(COMMISSION_RATE_FOR_THE_FIRST_10_K);
  } else {
    const commissionForTheFirstTenThousandUSD = new Decimal(20);
    const commissionForTheAmountAfterTenThousand = fullTransactionFaceAmount.amount
      .minus(TEN_THOUSAND_USD)
      .mul(COMMISSION_RATE_AFTER_THE_FIRST_10_K);
    commissionAmount = commissionForTheFirstTenThousandUSD.plus(commissionForTheAmountAfterTenThousand);
  }

  if (commissionAmount.greaterThanOrEqualTo(MAX_COMMISSION)) {
    return usd(MAX_COMMISSION);
  }

  if (commissionAmount.lessThanOrEqualTo(MIN_COMMISSION)) {
    return usd(MIN_COMMISSION);
  }
  return usd(commissionAmount);
}

/*  *Ad Versioning of Goodfill transaction pricing:*

  it is dangerous to compare just calculated values on a case-by-case basis
  because two math functions f(x) and g(x) can share the values for almost all x,
  differing only for special cases that may be omitted in testing or even in production usage for a long time.


  As a solution I am proposing adding a field commissionPricingVersion that explicitly references the version
  and can be used for explicit validation on the backend.

  Following the YAGNI principle, it makes sense to add it only once we change a transaction pricing model again.*/
export interface GoodfillTransactionCommission {
  transactionFaceAmount: Money;
  commissionAmount: Money;
  commissionRate: BondPriceNominal;
}
